import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Refresh,
  CheckCircle,
  Phone,
  Email,
  Message,
  Visibility,
  Edit,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  getAllInterestRequests,
  updateInterestRequestStatus,
  InterestRequest,
  getProduct,
} from '../../../services/request/Product';

const InterestRequestsManagement: React.FC = () => {
  const theme = useTheme();
  const [requests, setRequests] = useState<InterestRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedRequest, setSelectedRequest] = useState<InterestRequest | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [productName, setProductName] = useState<string>('');

  const STATUS_OPTIONS = [
    { value: 'INTEREST_RECEIVED', label: 'Interest Received', color: 'info' as const },
    { value: 'ACKNOWLEDGED', label: 'Acknowledged', color: 'warning' as const },
    { value: 'CONTACTED', label: 'Contacted', color: 'primary' as const },
    { value: 'CLOSED', label: 'Closed', color: 'success' as const },
  ];

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getAllInterestRequests(statusFilter || undefined);
      if (response && response.data) {
        const data = response.data;
        setRequests(data.result || data.requests || []);
      } else if (response && (response.result || response.requests)) {
        setRequests(response.result || response.requests || []);
      }
    } catch (err: any) {
      console.error('Error loading interest requests:', err);
      setError('Failed to load interest requests');
      toast.error('Failed to load interest requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleStatusUpdate = async (id: string, newStatus: string) => {
    try {
      await updateInterestRequestStatus(id, newStatus);
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
      loadRequests();
    } catch (err: any) {
      console.error('Error updating status:', err);
      toast.error('Failed to update status');
    }
  };

  const handleViewDetails = async (request: InterestRequest) => {
    setSelectedRequest(request);
    try {
      // Fetch product name
      const productResponse = await getProduct(request.productId);
      if (productResponse && productResponse.data) {
        setProductName(productResponse.data.name || 'Unknown Product');
      }
    } catch (err) {
      console.error('Error fetching product:', err);
      setProductName('Unknown Product');
    }
    setDetailDialogOpen(true);
  };

  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    const statusOption = STATUS_OPTIONS.find(opt => opt.value === status);
    return statusOption?.color || 'default';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ mb: 3, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" fontWeight="bold" color="primary">
            Interest Requests Management
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadRequests}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {/* Filter */}
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Status</InputLabel>
          <Select
            value={statusFilter}
            label="Filter by Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">All Statuses</MenuItem>
            {STATUS_OPTIONS.map((status) => (
              <MenuItem key={status.value} value={status.value}>
                {status.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Card>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ background: alpha(theme.palette.primary.main, 0.1) }}>
                <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Contact</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Product ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Message</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No interest requests found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((request) => (
                  <TableRow key={request.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {request.customerName || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {request.customerEmail && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Email fontSize="small" color="action" />
                            <Typography variant="caption">{request.customerEmail}</Typography>
                          </Box>
                        )}
                        {request.customerPhone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Phone fontSize="small" color="action" />
                            <Typography variant="caption">{request.customerPhone}</Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {request.productId?.substring(0, 8)}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={request.message || 'No message'}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: 200 }}>
                          <Message fontSize="small" color="action" />
                          <Typography variant="body2" noWrap>
                            {request.message ? (request.message.length > 30 ? `${request.message.substring(0, 30)}...` : request.message) : 'No message'}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(request.createdDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={request.status?.replace('_', ' ') || 'N/A'}
                        size="small"
                        color={getStatusColor(request.status)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleViewDetails(request)}>
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                          <Select
                            value={request.status || 'INTEREST_RECEIVED'}
                            onChange={(e) => handleStatusUpdate(request.id, e.target.value)}
                            sx={{ height: 32 }}
                          >
                            {STATUS_OPTIONS.map((status) => (
                              <MenuItem key={status.value} value={status.value}>
                                {status.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Interest Request Details
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Product</Typography>
                <Typography variant="body1" fontWeight="medium">{productName}</Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                  {selectedRequest.productId}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Customer Name</Typography>
                <Typography variant="body1">{selectedRequest.customerName || 'N/A'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Email</Typography>
                <Typography variant="body1">{selectedRequest.customerEmail || 'N/A'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Phone</Typography>
                <Typography variant="body1">{selectedRequest.customerPhone || 'N/A'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Message</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={selectedRequest.message || ''}
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={selectedRequest.status?.replace('_', ' ') || 'N/A'}
                    color={getStatusColor(selectedRequest.status)}
                  />
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Requested Date</Typography>
                <Typography variant="body1">{formatDate(selectedRequest.createdDate)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Last Updated</Typography>
                <Typography variant="body1">{formatDate(selectedRequest.updatedDate)}</Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
          {selectedRequest && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={selectedRequest.status || 'INTEREST_RECEIVED'}
                onChange={(e) => {
                  handleStatusUpdate(selectedRequest.id, e.target.value);
                  setSelectedRequest({ ...selectedRequest, status: e.target.value });
                }}
              >
                {STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InterestRequestsManagement;
